import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getPointsBalance, getPointsHistory, addPoints, removePoints, getLeaderboard, extractErrorMessage } from '../services/api';
import { FaPlus, FaMinus, FaTrophy, FaHistory } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';
import '../styles/Points.css';

function Points({ user }) {
  const toast = useToast();
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [savingAdd, setSavingAdd] = useState(false);
  const [formErrorAdd, setFormErrorAdd] = useState(null);
  const [savingRemove, setSavingRemove] = useState(false);
  const [formErrorRemove, setFormErrorRemove] = useState(null);
  const [pointsForm, setPointsForm] = useState({ user_id: '', amount: 0, reason: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [balanceData, historyData, leaderboardData] = await Promise.all([
        getPointsBalance(user.id),
        getPointsHistory(user.id, { limit: 50 }),
        getLeaderboard(10)
      ]);
      setBalance(balanceData.balance);
      setHistory(historyData.history);
      setLeaderboard(leaderboardData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(extractErrorMessage(error, 'Punkte-Daten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddPoints = async (e) => {
    e.preventDefault();
    if (savingAdd) return;
    setSavingAdd(true);
    setFormErrorAdd(null);
    try {
      await addPoints(pointsForm);
      setPointsForm({ user_id: '', amount: 0, reason: '' });
      setShowAddModal(false);
      toast.success('Punkte hinzugefügt');
      fetchData();
    } catch (error) {
      console.error('Error adding points:', error);
      setFormErrorAdd(extractErrorMessage(error, 'Fehler beim Hinzufügen der Punkte'));
    } finally {
      setSavingAdd(false);
    }
  };

  const handleRemovePoints = async (e) => {
    e.preventDefault();
    if (savingRemove) return;
    setSavingRemove(true);
    setFormErrorRemove(null);
    try {
      await removePoints(pointsForm);
      setPointsForm({ user_id: '', amount: 0, reason: '' });
      setShowRemoveModal(false);
      toast.success('Punkte abgezogen');
      fetchData();
    } catch (error) {
      console.error('Error removing points:', error);
      setFormErrorRemove(extractErrorMessage(error, 'Fehler beim Abziehen der Punkte'));
    } finally {
      setSavingRemove(false);
    }
  };

  const closeAddModal = () => {
    if (savingAdd) return;
    setShowAddModal(false);
    setFormErrorAdd(null);
  };

  const closeRemoveModal = () => {
    if (savingRemove) return;
    setShowRemoveModal(false);
    setFormErrorRemove(null);
  };

  const getPositiveNegativeClass = (amount) => {
    return amount >= 0 ? 'positive' : 'negative';
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Punkte</h1>
          {user.isAdmin && (
            <div className="header-actions">
              <button className="add-btn positive" onClick={() => setShowAddModal(true)}>
                <FaPlus /> Punkte hinzufügen
              </button>
              <button className="add-btn negative" onClick={() => setShowRemoveModal(true)}>
                <FaMinus /> Punkte abziehen
              </button>
            </div>
          )}
        </div>

        <div className="balance-card">
          <div className="balance-label">Dein Punktestand</div>
          <div className="balance-amount">{balance}</div>
          <div className="balance-unit">Punkte</div>
        </div>

        <div className="tab-buttons">
          <button className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>
            <FaHistory /> Verlauf
          </button>
          <button className={activeTab === 'leaderboard' ? 'active' : ''} onClick={() => setActiveTab('leaderboard')}>
            <FaTrophy /> Rangliste
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'overview' ? (
          <div className="history-section">
            <h2>Punkte-Verlauf</h2>
            {history.length > 0 ? (
              <div className="history-list">
                {history.map(entry => (
                  <div key={entry.id} className={`history-item ${getPositiveNegativeClass(entry.amount)}`}>
                    <div className="history-amount">
                      {entry.amount >= 0 ? '+' : ''}{entry.amount}
                    </div>
                    <div className="history-details">
                      <div className="history-reason">{entry.reason}</div>
                      <div className="history-date">
                        {new Date(entry.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">Noch keine Punkte-Aktivitäten</div>
            )}
          </div>
        ) : (
          <div className="leaderboard-section">
            <h2>Rangliste</h2>
            {leaderboard.length > 0 ? (
              <div className="leaderboard-list">
                {leaderboard.map((entry, index) => (
                  <div key={entry.user_id} className={`leaderboard-item ${index < 3 ? 'top-three' : ''}`}>
                    <div className="rank">
                      {index === 0 && <span className="medal gold">🥇</span>}
                      {index === 1 && <span className="medal silver">🥈</span>}
                      {index === 2 && <span className="medal bronze">🥉</span>}
                      {index > 2 && <span className="rank-number">{index + 1}</span>}
                    </div>
                    <div className="user-info">
                      <span className="user-id">{entry.user_id}</span>
                    </div>
                    <div className="points-value">{entry.total} Punkte</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">Keine Daten für die Rangliste</div>
            )}
          </div>
        )}

        {/* Add Points Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={closeAddModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Punkte hinzufügen</h2>
              {formErrorAdd && <div className="shared-form-error-banner">{formErrorAdd}</div>}
              <form onSubmit={handleAddPoints}>
                <div className="form-group">
                  <UserSelect
                    value={pointsForm.user_id}
                    onChange={id => setPointsForm({...pointsForm, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Anzahl *</label>
                  <input
                    type="number"
                    value={pointsForm.amount}
                    onChange={e => setPointsForm({...pointsForm, amount: parseInt(e.target.value) || 0})}
                    required
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Grund</label>
                  <input
                    type="text"
                    value={pointsForm.reason}
                    onChange={e => setPointsForm({...pointsForm, reason: e.target.value})}
                    placeholder="z.B. Bonus für gute Leistung"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeAddModal} disabled={savingAdd}>Abbrechen</button>
                  <button type="submit" className="submit-btn positive" disabled={savingAdd}>{savingAdd ? 'Speichert…' : 'Hinzufügen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Remove Points Modal */}
        {showRemoveModal && (
          <div className="modal-overlay" onClick={closeRemoveModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Punkte abziehen</h2>
              {formErrorRemove && <div className="shared-form-error-banner">{formErrorRemove}</div>}
              <form onSubmit={handleRemovePoints}>
                <div className="form-group">
                  <UserSelect
                    value={pointsForm.user_id}
                    onChange={id => setPointsForm({...pointsForm, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Anzahl *</label>
                  <input
                    type="number"
                    value={pointsForm.amount}
                    onChange={e => setPointsForm({...pointsForm, amount: parseInt(e.target.value) || 0})}
                    required
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Grund</label>
                  <input
                    type="text"
                    value={pointsForm.reason}
                    onChange={e => setPointsForm({...pointsForm, reason: e.target.value})}
                    placeholder="z.B. Strafe für Verstoß"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeRemoveModal} disabled={savingRemove}>Abbrechen</button>
                  <button type="submit" className="submit-btn negative" disabled={savingRemove}>{savingRemove ? 'Speichert…' : 'Abziehen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Points;
